import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Wallet, CheckCircle2, AlertTriangle, XCircle, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => (n === null || n === undefined ? "—" : `R ${Number(n).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(2)}%`);

const VERDICT = {
  affordable: { color: "bg-green-100 text-green-800 border-green-200", Icon: CheckCircle2, label: "AFFORDABLE" },
  tight: { color: "bg-amber-100 text-amber-800 border-amber-200", Icon: AlertTriangle, label: "TIGHT" },
  not_affordable: { color: "bg-red-100 text-red-800 border-red-200", Icon: XCircle, label: "NOT AFFORDABLE" },
};

export default function RaiseAffordability() {
  const [monthlyPool, setMonthlyPool] = useState(5000);
  const [loading, setLoading] = useState(15);
  const [months, setMonths] = useState(12);
  const [wagesOverride, setWagesOverride] = useState("");
  const [targetWagesGp, setTargetWagesGp] = useState(45);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const fetchData = async () => {
    setBusy(true);
    try {
      const p = new URLSearchParams();
      p.set("proposed_monthly_pool", Number(monthlyPool) || 0);
      p.set("employer_loading_pct", Number(loading) || 0);
      p.set("months", Number(months) || 12);
      p.set("target_wages_to_gp_pct", Number(targetWagesGp) || 45);
      if (wagesOverride !== "" && wagesOverride !== null) p.set("current_wages_override", Number(wagesOverride));
      const res = await axios.get(`${API}/affordability/analyze?${p.toString()}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to compute affordability");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { fetchData(); /* initial */ /* eslint-disable-next-line */ }, []);

  const cur = data?.current;
  const prop = data?.proposal;
  const impact = data?.impact;
  const verdictDef = data && VERDICT[data.verdict];

  return (
    <div className="p-6 space-y-6" data-testid="affordability-page">
      <div className="flex items-center gap-3">
        <Wallet className="h-7 w-7 text-emerald-600" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Raise / Bonus Affordability</h1>
          <p className="text-sm text-gray-500">Can you afford a salary increase or a monthly bonus pool? Test it against your trailing 12-month numbers.</p>
        </div>
      </div>

      {/* INPUTS */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Proposal</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-5 gap-3 items-end">
          <div>
            <Label>Monthly raise pool (R)</Label>
            <Input type="number" step="100" value={monthlyPool} onChange={(e) => setMonthlyPool(e.target.value)} data-testid="input-pool" />
            <p className="text-[11px] text-gray-500 mt-1">Total gross R across all staff</p>
          </div>
          <div>
            <Label>Employer loading %</Label>
            <Input type="number" step="0.5" value={loading} onChange={(e) => setLoading(e.target.value)} data-testid="input-loading" />
            <p className="text-[11px] text-gray-500 mt-1">UIF/SDL/WC/13th — 15% typical</p>
          </div>
          <div>
            <Label>Trailing window (months)</Label>
            <Input type="number" value={months} onChange={(e) => setMonths(e.target.value)} />
          </div>
          <div>
            <Label>Current wages override (annual, optional)</Label>
            <Input type="number" placeholder={cur?.wages_detected ? `Auto: ${cur.wages_detected}` : "Auto-detect"} value={wagesOverride} onChange={(e) => setWagesOverride(e.target.value)} data-testid="input-wages-override" />
          </div>
          <div className="flex items-end">
            <Button onClick={fetchData} disabled={busy} className="w-full" data-testid="btn-recalc">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <TrendingUp className="h-4 w-4 mr-1" />} Recalculate
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* VERDICT */}
      {data && verdictDef && (
        <Card className={`border-2 ${verdictDef.color}`}>
          <CardContent className="pt-4 flex items-start gap-3">
            <verdictDef.Icon className="h-7 w-7 mt-1" />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider opacity-70">Verdict</div>
              <div className="text-2xl font-bold" data-testid="verdict">{verdictDef.label}</div>
              <ul className="mt-2 list-disc pl-5 space-y-1 text-sm">
                {data.reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* CURRENT + IMPACT */}
      {data && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Current trailing {data.period.months}m</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row label="Net Sales (excl VAT)" value={fmt(cur.net_sales_excl_vat)} />
              <Row label="COGS" value={fmt(cur.cogs)} />
              <Row label="Gross Profit" value={fmt(cur.gross_profit)} sub={`${pct(cur.gp_margin_pct)} margin`} />
              <Row label="OPEX" value={fmt(cur.opex)} />
              <Row label="Net Profit" value={fmt(cur.net_profit)} bold valueClass={cur.net_profit >= 0 ? "text-green-700" : "text-red-700"} />
              <div className="border-t pt-2 mt-2">
                <Row label="Wages used (annual)" value={fmt(cur.wages_used)} />
                <Row label="Wages-to-GP" value={pct(cur.wages_to_gp_pct)} />
                <Row label="Wages-to-Sales" value={pct(cur.wages_to_sales_pct)} />
                {cur.wages_detected_categories?.length > 0 && (
                  <div className="text-[11px] text-gray-500 mt-1">
                    Auto-detected from: {cur.wages_detected_categories.map(c => c.category).join(", ")}
                  </div>
                )}
                {cur.wages_used === 0 && (
                  <div className="text-xs text-amber-700 mt-1">No wage categories auto-detected. Use the "override" input above for accurate analysis.</div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Proposal impact</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row label="Monthly pool" value={fmt(prop.monthly_pool)} />
              <Row label="Annual gross" value={fmt(prop.annual_gross)} />
              <Row label={`Employer loading (${prop.employer_loading_pct}%)`} value={`× ${(1 + prop.employer_loading_pct/100).toFixed(2)}`} />
              <Row label="True annual cost" value={fmt(prop.true_annual_cost)} bold />
              <div className="border-t pt-2 mt-2 space-y-2">
                <Row label="New Net Profit" value={fmt(impact.new_net_profit)} bold valueClass={impact.new_net_profit >= 0 ? "text-green-700" : "text-red-700"} />
                <Row label="New Wages-to-GP" value={pct(impact.new_wages_to_gp_pct)} valueClass={(impact.new_wages_to_gp_pct ?? 0) > 55 ? "text-red-700" : (impact.new_wages_to_gp_pct ?? 0) > 45 ? "text-amber-700" : "text-green-700"} />
                <Row label="New Wages-to-Sales" value={pct(impact.new_wages_to_sales_pct)} />
                <Row label="Markup lift required to fund" value={impact.markup_pp_required_to_fund !== null ? `+${impact.markup_pp_required_to_fund} pp` : "—"} sub="on average cost" />
                <Row label="New break-even sales" value={fmt(impact.new_breakeven_sales)} />
                <Row label="Cushion vs break-even" value={`${fmt(impact.breakeven_cushion_rand)} (${pct(impact.breakeven_cushion_pct)})`} valueClass={(impact.breakeven_cushion_rand ?? 0) < 0 ? "text-red-700" : "text-green-700"} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* NOTES */}
      {data && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">How the math works</CardTitle></CardHeader>
          <CardContent>
            <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
              {data.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Row({ label, value, sub, bold, valueClass = "" }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-gray-600">{label}</span>
      <span className={`text-right ${bold ? "font-semibold" : ""} ${valueClass}`}>
        {value}
        {sub && <span className="block text-[11px] text-gray-500 font-normal">{sub}</span>}
      </span>
    </div>
  );
}
